import dayjs from "dayjs"
import { getUserFullName } from "../../../helpers/userDisplay"

/** Relations required for Admin Inbox call detail UI. */
export const ADMIN_CALL_DETAIL_WITH = [
  "user",
  "projects",
  "categories",
  "cities",
  "media",
]

/** Relations required for Inbox & Calls table columns only. */
export const CALL_LIST_WITH = ["user", "categories", "cities"]

export const CALL_TYPE_OPTIONS = [
  { value: "informal", label: "Informal" },
  { value: "formal", label: "Formal" },
]

export const DEFAULT_CALL_TYPE = "informal"

export const CALL_STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "pending", label: "Pending" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" },
  { value: "rejected", label: "Rejected" },
  { value: "draft", label: "Draft" },
]

export const STATUS_LABEL = {
  published: "Published",
  pending: "Pending",
  closed: "Closed",
  archived: "Archived",
  rejected: "Rejected",
  draft: "Draft",
  active: "Active",
}

/** Formal call moderation → API status for PATCH /admin/calls/update/:id */
export const FORMAL_CALL_MODERATION_STATUS = {
  approved: "published",
  rejected: "rejected",
}

/** Formal Inbox status tabs — same model as Manage City Calls. */
export const FORMAL_CALL_STATUS_TABS = [
  { key: "all", label: "All", countTone: "muted" },
  {
    key: "awaiting",
    label: "Awaiting Approval",
    countTone: "awaiting",
  },
  { key: "approved", label: "Approved", countTone: "approved" },
  { key: "rejected", label: "Rejected", countTone: "rejected" },
]

/** UI tab key → API status query value. */
export const FORMAL_CALL_TAB_TO_API_STATUS = {
  awaiting: "pending",
  approved: "published",
  rejected: "rejected",
}

export const isFormalCallAwaitingApproval = (row, callType) =>
  callType === "formal" &&
  (row?.status === "pending" || row?.status === "draft")

/**
 * Map GET /api/v1/admin/calls/summary → Formal Inbox status tabs.
 */
export const mapFormalCallStatusTabs = (summary = {}) => {
  const awaiting = Number(summary.pending) || 0
  const approved = Number(summary.published) || 0
  const rejected =
    Number(summary.rejected ?? summary.rejected_closed_archived) || 0
  const all =
    Number(summary.total_requests) || awaiting + approved + rejected

  const counts = {
    all,
    awaiting,
    approved,
    rejected,
  }

  return FORMAL_CALL_STATUS_TABS.map(tab => ({
    ...tab,
    count: counts[tab.key] ?? 0,
  }))
}

const formatCount = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("en-US")
}

const formatPostedDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "--"
}

const posterName = user => {
  if (!user) return "--"
  return getUserFullName(user, { fallback: "--" })
}

const posterAvatar = user =>
  user?.profile_media?.image ||
  user?.profile_media?.thumbnail ||
  user?.image ||
  ""

const categoryLabel = categories => {
  if (!Array.isArray(categories) || categories.length === 0) return "--"
  return categories
    .map(cat => cat?.name)
    .filter(Boolean)
    .join(", ")
}

const cityLabel = (cities, isGlobal) => {
  if (isGlobal) return "All cities"
  if (!Array.isArray(cities) || cities.length === 0) return "--"
  return cities
    .map(city => {
      const parts = [city?.city, city?.state || city?.country].filter(Boolean)
      return parts.join(", ")
    })
    .filter(Boolean)
    .join("; ")
}

const callTitle = item => {
  const title = String(item?.title || "").trim()
  if (title) return title
  const details = String(item?.details || "").trim()
  if (details) return details
  return "--"
}

const normalizeStatus = status =>
  String(status || "")
    .trim()
    .toLowerCase()

/**
 * Map GET /api/v1/admin/calls/summary → KPI cards (by type).
 */
export const mapCallsSummaryKpi = (result = {}, type = DEFAULT_CALL_TYPE) => {
  if (type === "formal") {
    return [
      {
        key: "total_requests",
        title: "Total Requests",
        value: formatCount(result.total_requests),
        tone: "lightblue-custom",
      },
      {
        key: "pending",
        title: "Pending",
        value: formatCount(result.pending),
        tone: "peach",
      },
      {
        key: "published",
        title: "Published",
        value: formatCount(result.published),
        tone: "lightteal",
      },
      {
        key: "rejected_closed_archived",
        title: "Rejected / Closed / Archived",
        value: formatCount(result.rejected_closed_archived),
        tone: "purple-custom",
      },
    ]
  }

  return [
    {
      key: "total_informal",
      title: "Total Inbox & Calls",
      value: formatCount(result.total_informal_inboxes_calls),
      tone: "lightblue-custom",
    },
    {
      key: "published",
      title: "Published",
      value: formatCount(result.published),
      tone: "lightteal",
    },
    {
      key: "closed_archived",
      title: "Closed / Archived",
      value: formatCount(result.closed_archived),
      tone: "purple-custom",
    },
  ]
}

/**
 * Map GET /api/v1/admin/calls item → table row.
 */
export const mapCallRow = item => {
  const status = normalizeStatus(item?.status)
  const user = item?.user || null

  return {
    id: item?.id,
    type: item?.type || "",
    poster: {
      name: posterName(user),
      avatar: posterAvatar(user),
    },
    title: callTitle(item),
    category: categoryLabel(item?.categories),
    city: cityLabel(item?.cities, Boolean(item?.is_global)),
    replies: Number.isFinite(Number(item?.total_responses))
      ? Number(item.total_responses)
      : 0,
    status: status || "published",
    postedDate: formatPostedDate(item?.published_at || item?.created_at),
    raw: item,
  }
}
